// ─── 💶 Il conto coi maestri (tappa ⑧b) ──────────────────────────────────────
//
// 🗣️ *«io affitto il campo al maestro […] costo della lezione oppure del corso,
// costo del campo, la differenza va al maestro»* · *«incasso io dagli allievi,
// non il maestro»* · *«io si intende la segreteria»* (20/09/2026).
//
// 🎯 **Questa schermata risponde a UNA domanda: quanto devo dare a chi, questo
//    mese.** Tutto il resto — le ore, il campo, le volte ancora da fare — è il
//    modo di spiegare quella cifra quando qualcuno la contesta.
//
// ⭐⭐ **E LA COSA PIÙ IMPORTANTE È QUELLO CHE NON FA: non paga nessuno.** Dice
//    quanto è maturato, non che sia stato versato. Finché non esiste un libro
//    dei pagamenti ai maestri, «Da pagare» è una cifra da leggere, non una riga
//    che si spunta — chi paga se lo segna fuori di qui.
//    📌 *Una schermata che somiglia a un registro dei pagamenti senza esserlo è
//       peggio di una che non c'è: la seconda non la crede nessuno.*

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents, type ContoDeiMaestri, type RigaContoMaestro } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { useQuery } from '@tanstack/react-query';
import { Attesa, Guasto, Vuoto } from '../../components/pezzi.js';
import { useSessione } from '../../features/auth/sessione.js';
import { giornoSenzaAnno, inItaliano, mesePerEsteso, oggi, piuMesi } from '../../lib/time.js';

/**
 * Le ore in DECIMALI — `8`, `7,5` — e non `7 h 30′`.
 *
 * ⚖️ Non è un doppione di `durataScritta` (⇒ `features/calendario/Griglia.tsx`),
 * che dice la stessa cosa in un'altra forma: quella si **legge**, questa si
 * **moltiplica**. È la colonna con cui chi controlla il conto rifà il calcolo —
 * *ore × tariffa = campo* — e `7 h 30′` non si moltiplica per niente.
 * ⚠️ Il conto torna **quando nel mese è valsa una tariffa sola**: con due, il
 *    prodotto non deve tornare, e la riga sotto la tabella lo dice.
 */
function oreDecimali(minuti: number): string {
  return (minuti / 60).toLocaleString('it-IT', { maximumFractionDigits: 1 });
}

const CHIAVE = 'conto-maestri';

export function ContoMaestri(): React.JSX.Element {
  const [mese, setMese] = useState(() => oggi().slice(0, 7));

  const conto = useQuery({
    queryKey: [CHIAVE, mese],
    queryFn: () => chiama<ContoDeiMaestri>('GET', `/conto/maestri?mese=${mese}`),
    /* 🗓️ Il mese di prima resta a schermo mentre arriva quello nuovo, come in
       «I soldi» del maestro: senza, ogni freccia butterebbe via la tabella e
       rimetterebbe «Sto leggendo…». ⇒ E per questo il nome del mese si legge
       da `c.mese`, cioè dalla RISPOSTA: l'intestazione e le cifre restano
       d'accordo anche in mezzo al giro.
       📌 *Un'intestazione che cambia prima dei numeri che intesta è il modo più
          semplice di far leggere la cifra sbagliata.* */
    placeholderData: (prima) => prima,
  });

  return (
    <section className="conto-maestri">
      {/* 🗂️ Il titolo non c'è più: dal 24/09/2026 (voce 23) questa è la
          linguetta «Maestri» della Cassa, e il nome lo dice la linguetta. */}
      <header className="testata">
        <p className="tenue">
          Quanto resta al maestro dopo l’ora di campo. Gli allievi pagano al circolo, il circolo trattiene il
          campo.
        </p>
      </header>

      {conto.isLoading ? (
        <Attesa cosa="il conto del mese" />
      ) : conto.error ? (
        <Guasto errore={conto.error} />
      ) : (
        <Dentro c={conto.data!} vaiA={setMese} inArrivo={conto.isFetching} />
      )}
    </section>
  );
}

function Dentro({
  c,
  vaiA,
  inArrivo,
}: {
  c: ContoDeiMaestri;
  vaiA: (m: string) => void;
  inArrivo: boolean;
}): React.JSX.Element {
  const { profilo } = useSessione();
  const puòScrivereLaTariffa = profilo?.permissions.includes('manage_settings') ?? false;

  const questoMese = oggi().slice(0, 7);
  /* ⛔ Le frecce si spengono anche MENTRE il mese arriva: il passo si calcola da
     `c.mese`, cioè dalla risposta — due tocchi rapidi partirebbero tutti e due
     dallo stesso mese e il secondo non andrebbe da nessuna parte. */
  const primo = c.primo !== null && c.mese <= c.primo;
  const ultimo = c.ultimo !== null && c.mese >= c.ultimo;

  // 🆕 Fase 3 (R6): conta anche chi nel mese ha solo delle trattenute.
  const contate = c.righe.filter((r) => r.volte > 0 || r.trattenuteCents > 0);
  const conTrattenute = c.trattenute.length > 0;
  const scoperte = c.righe.reduce((t, r) => t + r.scoperte, 0);
  const tot = totale(contate);

  return (
    <>
      <div className="navigazione mese-scelto" aria-busy={inArrivo}>
        <button
          type="button"
          className="piatto freccia"
          disabled={primo || inArrivo}
          title={`Vai a ${mesePerEsteso(piuMesi(c.mese, -1))}`}
          aria-label={`Vai a ${mesePerEsteso(piuMesi(c.mese, -1))}`}
          onClick={() => vaiA(piuMesi(c.mese, -1))}
        >
          ←
        </button>
        <strong>{mesePerEsteso(c.mese)}</strong>
        <button
          type="button"
          className="piatto freccia"
          disabled={ultimo || inArrivo}
          title={`Vai a ${mesePerEsteso(piuMesi(c.mese, 1))}`}
          aria-label={`Vai a ${mesePerEsteso(piuMesi(c.mese, 1))}`}
          onClick={() => vaiA(piuMesi(c.mese, 1))}
        >
          →
        </button>
        <button
          type="button"
          className="piatto"
          disabled={c.mese === questoMese || inArrivo}
          onClick={() => vaiA(questoMese)}
        >
          Questo mese
        </button>
      </div>

      {c.righe.length === 0 ? (
        /*
         * 👁️ **«NIENTE» DEV'ESSERE VERO ANCHE PER CHI GUARDA IL CALENDARIO, e
         *    alla prima stesura non lo era.** Su un mese futuro qui si leggeva
         *    *«niente in questo mese»* mentre in calendario c'erano due corsi
         *    già prenotati: vero per il conto (nessuna volta è finita), falso
         *    per chi apre il mese prossimo proprio per sapere cosa maturerà —
         *    e chi legge «niente» non va a controllare.
         * 📌 *Una schermata che dice «non c'è niente» deve intendere la stessa
         *    cosa che intende chi lo legge, o è una frase vera che informa male.*
         */
        <Vuoto
          titolo="Niente di già fatto in questo mese"
          perché={
            c.daFareVolte === 0
              ? 'Nessun maestro ha niente in questo mese.'
              : c.daFareVolte === 1
                ? 'In calendario c’è 1 volta con un maestro, ma non è ancora finita: il conto conta ciò che è già stato fatto, perché è quello il campo che è stato usato.'
                : `In calendario ci sono ${c.daFareVolte} volte con un maestro, ma nessuna è ancora finita: il conto conta ciò che è già stato fatto, perché è quello il campo che è stato usato.`
          }
        />
      ) : contate.length === 0 ? (
        <SenzaTariffa c={c} quante={scoperte} puòScrivere={puòScrivereLaTariffa} />
      ) : (
        <>
          <p className="tenue piccolo">
            Le volte <strong>già fatte</strong>
            {c.mese === questoMese ? `, al ${giornoSenzaAnno(oggi())}` : ''}.
          </p>

          <div className="tabella-scorre">
            <table className="tabella">
              <thead>
                <tr>
                  <th>Maestro</th>
                  <th className="a-destra">Volte</th>
                  <th className="a-destra">Ore</th>
                  <th className="a-destra">A carico allievi</th>
                  <th className="a-destra">Campo</th>
                  {conTrattenute && <th className="a-destra">Trattenute</th>}
                  <th className="a-destra">Da pagare</th>
                </tr>
              </thead>
              <tbody>
                {contate.map((r) => (
                  <tr key={r.maestroId}>
                    <td>
                      {r.nome}
                      {/* 🚨 Le volte fuori dal conto si dicono NELLA RIGA di chi
                          le ha fatte, non solo nel totale in fondo: è lui quello
                          a cui manca qualcosa, ed è la sua riga che qualcuno
                          guarda quando il conto non gli torna. */}
                      {r.scoperte > 0 && (
                        <span className="tenue piccolo">
                          {' '}
                          · {r.scoperte} {r.scoperte === 1 ? 'volta' : 'volte'} fuori dal conto
                        </span>
                      )}
                    </td>
                    <td className="a-destra">{r.volte}</td>
                    <td className="a-destra">{oreDecimali(r.minuti)}</td>
                    <td className="a-destra">{formatCents(r.allieviCents)}</td>
                    <td className="a-destra">{formatCents(r.campoCents)}</td>
                    {conTrattenute && (
                      <td className="a-destra">
                        {r.trattenuteCents > 0 ? `− ${formatCents(r.trattenuteCents)}` : '—'}
                      </td>
                    )}
                    <td className="a-destra">
                      <strong>{formatCents(r.restaCents)}</strong>
                    </td>
                  </tr>
                ))}
                {/* 💶 «In tutto» si somma QUI e non arriva dal server: un totale
                    mandato da fuori è un numero che un giorno può non tornare
                    con le righe che lo compongono, e a leggerlo è qualcuno che
                    deve fidarsi. */}
                <tr className="somma">
                  <td>In tutto</td>
                  <td className="a-destra">{tot.volte}</td>
                  <td className="a-destra">{oreDecimali(tot.minuti)}</td>
                  <td className="a-destra">{formatCents(tot.allievi)}</td>
                  <td className="a-destra">{formatCents(tot.campo)}</td>
                  {conTrattenute && (
                    <td className="a-destra">
                      {tot.trattenute > 0 ? `− ${formatCents(tot.trattenute)}` : '—'}
                    </td>
                  )}
                  <td className="a-destra">
                    <strong>{formatCents(tot.resta)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {conTrattenute && (
            <div className="riquadro">
              <h3>🌙 Trattenute delle lezioni della sera</h3>
              <p className="tenue piccolo">
                Disdette a meno di 48 ore, o l’allievo che non si è presentato: 30 € dal compenso. Si tolgono
                dalla scheda della lezione, col motivo.
              </p>
              <ul>
                {c.trattenute.map((t) => (
                  <li key={t.id}>
                    <strong>{c.righe.find((r) => r.maestroId === t.instructorId)?.nome ?? 'Maestro'}</strong>{' '}
                    · {giornoSenzaAnno(t.date)} alle {t.time}, {t.courtName}
                    {t.students ? ` con ${t.students}` : ''} ·{' '}
                    {t.reason === 'no_show'
                      ? 'l’allievo non si è presentato'
                      : `disdetta ${t.hoursBefore ?? 0} ore prima`}{' '}
                    · <strong>− {formatCents(t.amountCents)}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="tenue piccolo">
            Da pagare ai maestri: <strong>{formatCents(tot.resta)}</strong>. Gli allievi hanno a carico{' '}
            <strong>{formatCents(tot.allievi)}</strong>, e il campo resta al circolo:{' '}
            <strong>{formatCents(tot.campo)}</strong>.
          </p>

          {/* 🧾⭐ La cifra all'ora si NOMINA solo quando nel mese ne è valsa una
              sola. Con due, «20,00 € all'ora» sarebbe vero per metà mese e falso
              per l'altra — e chi controlla moltiplicando le ore non ritroverebbe
              il totale, concludendo che la schermata sbaglia.
              📌 *Una frase vera a metà, in una schermata di soldi, si comporta
                 come una frase falsa: manda a cercare un errore che non c'è.* */}
          {c.tariffe.length === 1 ? (
            <p className="tenue piccolo">
              Il campo costa <strong>{formatCents(c.tariffe[0]!.centsPerHour)} all’ora</strong>, dal{' '}
              {inItaliano(c.tariffe[0]!.validFrom)}.
            </p>
          ) : c.tariffe.length > 1 ? (
            <p className="tenue piccolo">
              In questo mese sono valse <strong>{c.tariffe.length} tariffe diverse</strong> (
              {c.tariffe.map((t) => formatCents(t.centsPerHour)).join(' · ')} all’ora): le ore per una tariffa
              sola non rifanno il totale, che è la somma volta per volta.
            </p>
          ) : null}

          {c.daFareVolte > 0 && (
            <p className="tenue piccolo">
              Ci sono ancora <strong>{c.daFareVolte}</strong>{' '}
              {c.daFareVolte === 1 ? 'volta da fare' : 'volte da fare'} questo mese: non sono contate qui,
              perché quel campo non l’ha ancora usato nessuno.
            </p>
          )}

          {scoperte > 0 && (
            <p className="tenue piccolo">
              <strong>
                {scoperte} {scoperte === 1 ? 'volta è' : 'volte sono'} fuori dal conto
              </strong>
              : il listino dei maestri non ha un prezzo del campo per{' '}
              {scoperte === 1 ? 'quell’ora' : 'quelle ore'} (non c’era ancora, o ha delle caselle vuote), e{' '}
              {scoperte === 1 ? 'non porta' : 'non portano'} né il proprio incasso né il proprio costo.
              Contarle a campo zero direbbe che al maestro spetta tutto.
              {puòScrivereLaTariffa && (
                <>
                  {' '}
                  <Link to="/impostazioni/campo-ai-maestri">Completa il listino dei maestri</Link>.
                </>
              )}
            </p>
          )}

          <p className="tenue piccolo">
            Qui non si paga nessuno: è quanto è <strong>maturato</strong>, non quanto è stato versato.
          </p>
        </>
      )}
    </>
  );
}

/**
 * ⛔⭐⭐ **SENZA TARIFFA IL CONTO NON SI FA, E LO DICE.**
 *
 * La cosa facile sarebbe contare il campo **zero** finché nessuno l'ha scritta:
 * e allora questa schermata direbbe, con dei numeri perfettamente formattati,
 * che ai maestri spetta **tutto** l'incasso degli allievi.
 * 📌 *Un dato che manca si dichiara mancante: arrotondato a zero diventa una
 *    risposta sbagliata invece di una domanda.*
 *
 * 🔀 **Due casi diversi, e si dicono diversi**: non c'è **nessuna** tariffa, o
 *    ce n'è una che comincia **dopo** queste volte. Il secondo si corregge
 *    scrivendone una con una data più indietro, il primo scrivendone una e
 *    basta — e chi legge deve sapere quale dei due gli è capitato.
 */
function SenzaTariffa({
  c,
  quante,
  puòScrivere,
}: {
  c: ContoDeiMaestri;
  quante: number;
  puòScrivere: boolean;
}): React.JSX.Element {
  const nessuna = c.tariffeInTutto === 0;
  return (
    <>
      <div className="vuoto">
        <strong>
          {nessuna ? 'Non c’è nessun listino dei maestri.' : 'Il listino dei maestri non copre queste volte.'}
        </strong>
        <p className="tenue">
          Finché il listino dei maestri non ha un prezzo per quei giorni e quelle ore, il conto non si fa.
          Contare il campo <strong>zero</strong> direbbe che ai maestri spetta <strong>tutto</strong>{' '}
          l’incasso degli allievi — e lo direbbe con dei numeri, che è il modo peggiore di sbagliare.
        </p>
      </div>
      <p className="tenue piccolo">
        Le <strong>{quante}</strong>{' '}
        {quante === 1 ? 'volta già fatta resta contata' : 'volte già fatte restano contate'}: appena il
        listino le copre, il conto compare per intero.
      </p>
      {puòScrivere && (
        <p>
          <Link to="/impostazioni/campo-ai-maestri">Apri il listino dei maestri</Link>
        </p>
      )}
    </>
  );
}

/** I cinque totali, sommati dalle righe che si vedono. */
function totale(righe: RigaContoMaestro[]): {
  volte: number;
  minuti: number;
  allievi: number;
  campo: number;
  trattenute: number;
  resta: number;
} {
  return righe.reduce(
    (t, r) => ({
      volte: t.volte + r.volte,
      minuti: t.minuti + r.minuti,
      allievi: t.allievi + r.allieviCents,
      campo: t.campo + r.campoCents,
      trattenute: t.trattenute + r.trattenuteCents,
      resta: t.resta + r.restaCents,
    }),
    { volte: 0, minuti: 0, allievi: 0, campo: 0, trattenute: 0, resta: 0 },
  );
}
